/**
 * Interactive-state design tokens (UX 16.4 / 9.1).
 *
 * Single source of truth for how a control communicates its three runtime
 * states — enabled, disabled, busy/loading — so an *active* control is never
 * mistaken for a disabled one. The Vuetify theme (`main.ts`) registers these
 * as theme variables (CSS custom properties) and `AppButton.vue` consumes the
 * same vars, so a button rendered through Vuetify and a button rendered through
 * AppButton stay visually consistent.
 *
 * ── WCAG 2.1 rationale ───────────────────────────────────────────────────────
 *   • Disabled — opacity 0.55 + `cursor: not-allowed`.
 *       WCAG 1.4.11 (Non-text Contrast) exempts *inactive* controls from the
 *       3:1 requirement, but 16.4/9.1 demands the disabled state still be
 *       perceivable AND clearly different from enabled. 0.55 dims the control
 *       enough that, on the app's light surfaces, the enabled↔disabled change
 *       far exceeds the 3:1 "perceivable difference" boundary (luminance drops
 *       ~45%), while staying high enough that the control itself remains
 *       visible (it does not vanish into the background). Using opacity rather
 *       than a flat grey preserves hue, so low-vision / magnifier users still
 *       recognise it as the same control.
 *   • Busy / loading — opacity 1 + spinner + `cursor: progress`.
 *       The busy state MUST NOT reuse the disabled dimming — a dimmed in-flight
 *       button is exactly the 16.4/9.1 bug (an active action looking broken).
 *       Full opacity + an animated spinner + `aria-busy="true"` reads as
 *       "working", and the `progress` cursor distinguishes it from the
 *       `not-allowed` disabled cursor even when motion is reduced.
 *   • Enabled — opacity 1, default/pointer cursor, no spinner.
 *       Guarantees an enabled control never carries any disabled treatment.
 *
 * jsdom cannot compute the resulting colours; tests assert these constants and
 * the rendered classes/attributes instead.
 */

export interface InteractiveStateToken {
    /** 0–1 multiplier applied to the control's appearance. */
    readonly opacity: number;
    /** CSS `cursor` value advertising what interaction is (un)available. */
    readonly cursor: string;
}

export interface InteractiveStateTokens {
    readonly enabled: InteractiveStateToken;
    readonly disabled: InteractiveStateToken;
    readonly loading: InteractiveStateToken;
}

export const INTERACTIVE_STATE_TOKENS: InteractiveStateTokens = {
    enabled: {
        opacity: 1,
        cursor: 'pointer',
    },
    disabled: {
        // Reduced but still perceivable; see WCAG rationale above.
        opacity: 0.55,
        cursor: 'not-allowed',
    },
    loading: {
        // Full opacity: visually DISTINCT from `disabled`. Never dim a busy
        // control — that is the 16.4 / 9.1 regression.
        opacity: 1,
        cursor: 'progress',
    },
} as const;

/**
 * CSS custom-property names backing the tokens above. Registered on the
 * Vuetify theme so platform-wide components can reference the same values,
 * e.g. `opacity: var(--app-state-disabled-opacity)`.
 */
export const INTERACTIVE_STATE_CSS_VARS = {
    disabledOpacity: '--app-state-disabled-opacity',
    disabledCursor: '--app-state-disabled-cursor',
    busyOpacity: '--app-state-busy-opacity',
    busyCursor: '--app-state-busy-cursor',
} as const;

/**
 * Flat `{ '--app-state-…': value }` map for spreading into a Vuetify theme's
 * `variables` block or an inline `style` binding. Numeric opacities are
 * stringified because CSS custom properties are always strings.
 */
export const interactiveStateThemeVariables: Record<string, string> = {
    [INTERACTIVE_STATE_CSS_VARS.disabledOpacity]: String(
        INTERACTIVE_STATE_TOKENS.disabled.opacity,
    ),
    [INTERACTIVE_STATE_CSS_VARS.disabledCursor]:
        INTERACTIVE_STATE_TOKENS.disabled.cursor,
    [INTERACTIVE_STATE_CSS_VARS.busyOpacity]: String(
        INTERACTIVE_STATE_TOKENS.loading.opacity,
    ),
    [INTERACTIVE_STATE_CSS_VARS.busyCursor]:
        INTERACTIVE_STATE_TOKENS.loading.cursor,
};
